// File: src/components/Cell.js

import React, { memo } from "react";
import CustomAutocomplete from "./CustomAutocomplete";
import { DateTime } from "luxon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown } from "@fortawesome/free-solid-svg-icons";

// contrast / darken helpers
const getContrastColor = (color) => {
  if (color[0] === "#") color = color.slice(1);
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? "black" : "white";
};
const darkenColor = (color, amount) => {
  let usePound = false;
  if (color[0] === "#") {
    color = color.slice(1);
    usePound = true;
  }
  let num = parseInt(color, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.floor(r * (1 - amount)));
  g = Math.max(0, Math.floor(g * (1 - amount)));
  b = Math.max(0, Math.floor(b * (1 - amount)));
  const newColor = (r << 16) | (g << 8) | b;
  return (usePound ? "#" : "") + newColor.toString(16).padStart(6, "0");
};

export const Cell = memo(function Cell({
  cell,
  row,
  rowIndex,
  columnIndex,
  columns,
  localData,
  editedData,
  editingCell,
  selectedRows,
  toggleRowSelection,
  existingCompanies,
  onCreateCompany,
  onOpenDetails,
  handleEditStart,
  handleEditChange,
  onCellUpdate,
  handleKeyDown,
  inputRef,
  onOpenAddressMenu,
}) {
  const column = columns.find((col) => col.accessor === cell.column.id);

  // Handle expand/collapse button for group rows
  if (column && column.accessor === "expandCollapse") {
    if (row.original.isGroupRow) {
      return (
        <div className="expand-collapse-container">
          <button 
            className="expand-collapse-button"
            onClick={(e) => {
              e.stopPropagation();
              // This component needs a toggleGroupExpansion prop
            }}
            title={row.original.isExpanded ? "Collapse" : "Expand"}
          >
            <FontAwesomeIcon 
              icon={row.original.isExpanded ? faChevronDown : faChevronRight} 
            />
          </button>
          <span className="group-count">{row.original.groupSize}</span>
        </div>
      );
    }
    return null;
  }

  // in Cell.js, under massSelection:
  if (column?.massSelection) {
    // For group rows, check if all children are selected
    if (row.original.isGroupRow) {
      // We don't have access to rowGroups here, so we'll rely on originalRows
      const originalRows = row.original.originalRows || [];
      const allChildrenSelected = originalRows.length > 0 && 
                                originalRows.every(childRow => selectedRows[childRow.id]);
      
      return (
        <input
          className="mass-selection-checkbox"
          type="checkbox"
          checked={allChildrenSelected}
          onChange={e => {
            e.stopPropagation();
            toggleRowSelection(row.original.id);
          }}
        />
      );
    }
    
    // For child rows or regular rows
    return (
      <input
        className="mass-selection-checkbox"
        type="checkbox"
        checked={!!selectedRows[row.original.id]}
        onChange={e => {
          e.stopPropagation();
          toggleRowSelection(row.original.id);
        }}
      />
    );
  }
  

  // 2) address
  if (column.accessor === "address") {
    const addr = cell.value || {
      address1: row.original.address1,
      address2: row.original.address2,
      city: row.original.city,
      state: row.original.state,
      zip: row.original.zip,
      county: row.original.county,
      country: row.original.country,
    };
    const left = [addr.address1, addr.address2, addr.city]
      .filter(Boolean)
      .join(" ");
    const right = [addr.state, addr.zip, addr.country || addr.county]
      .filter(Boolean)
      .join(" ");
    const display = left && right ? `${left}, ${right}` : left || right;

    return (
      <div
        onContextMenu={(e) => onOpenAddressMenu(e, addr, row.original.id)}
        onDoubleClick={(e) => onOpenAddressMenu(e, addr, row.original.id)}
      >
        <span>{display || "Click to add address"}</span>
      </div>
    );
  }

  // 3) company + autocomplete
  if (column.accessor === "company") {
    const editing =
      editingCell?.id === row.original.id && editingCell?.field === "company";
    const key = `${row.original.id}-company`;
    const val = (editedData[key] ?? cell.value) || "";
    const suggestions = existingCompanies
      .filter((c) => c.name.toLowerCase().includes(val.toLowerCase()))
      .map((c) => c.name);
    const valid = existingCompanies.some(
      (c) => c.name.trim().toLowerCase() === val.trim().toLowerCase()
    );
    const plusBtn = (
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCreateCompany(val, row.original);
        }}
        style={{
          position: "absolute",
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "0 4px",
          fontSize: "14px",
          color: "#000",
          marginLeft: "4px",
          lineHeight: 1,
          pointerEvents: "all",
        }}
        title="Add Company"
      >
        +
      </button>
    );

    if (editing) {
      return (
        <div style={{ position: "relative", display: "inline-block" }}>
          <CustomAutocomplete
            value={val}
            onChange={(v) => handleEditChange(row.original.id, "company", v)}
            onBlur={() => handleEditStart(null)}
            suggestions={suggestions}
            chipStyle
          />
          {val && !valid && plusBtn}
        </div>
      );
    }
    if (val) {
      return valid ? (
        <div
          onDoubleClick={() =>
            onOpenDetails
              ? onOpenDetails(row.original)
              : handleEditStart(row.original.id, "company")
          }
          style={{
            userSelect: "none",
            cursor: "pointer",
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "12px",
            backgroundColor: "#e0e0e0",
            position: "relative",
          }}
        >
          {val}
        </div>
      ) : (
        <div style={{ position: "relative", display: "inline-block" }}>
          <div
            onClick={() => handleEditStart(row.original.id, "company")}
            style={{ cursor: "text", paddingRight: "20px" }}
          >
            {val}
          </div>
          {plusBtn}
        </div>
      );
    }
    return (
      <div
        onClick={() => handleEditStart(row.original.id, "company")}
        style={{ cursor: "text", minHeight: "1.5em" }}
      />
    );
  }

  // 4) DropdownOptions
  if (column?.DropdownOptions) {
    const key = `${row.original.id}-${cell.column.id}`;
    const val = editedData[key] ?? cell.value;
    const bg = column.dropdownBackgroundColor
      ? column.dropdownBackgroundColor(val)
      : "#fff";
    const textColor = getContrastColor(bg);
    const borderColor = darkenColor(bg, 0.2);

    return (
      <select
        style={{
          backgroundColor: bg,
          color: textColor,
          border: `2px solid ${borderColor}`,
          width: "100%",
          boxSizing: "border-box",
        }}
        value={val}
        onChange={(e) =>
          handleEditChange(row.original.id, cell.column.id, e.target.value)
        }
        onBlur={() => handleEditStart(null)}
        onKeyDown={(e) => handleKeyDown(e, rowIndex, columnIndex)}
      >
        {column.DropdownOptions.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  // 5) custom column.Cell
  if (column?.Cell) {
    return column.Cell({
      value: cell.value,
      row,
      updateCell: handleEditChange,
      isEditing:
        editingCell?.id === row.original.id && editingCell?.field === cell.column.id,
    });
  }

  // 6) autocomplete
  if (column?.autocomplete) {
    const editing =
      editingCell?.id === row.original.id && editingCell?.field === cell.column.id;
    const key = `${row.original.id}-${cell.column.id}`;
    const val = editedData[key] ?? cell.value;
    const suggestions = Array.from(
      new Set(localData.map((i) => i[column.accessor]).filter((v) => v && v !== val))
    );
    const Aut = editing ? (
      <CustomAutocomplete
        value={val}
        onChange={(v) => handleEditChange(row.original.id, column.accessor, v)}
        onBlur={() => handleEditStart(null)}
        suggestions={suggestions}
      />
    ) : (
      <span onDoubleClick={() => handleEditStart(row.original.id, column.accessor)}>
        {cell.value}
      </span>
    );
    if (column.buttonOnHover) {
      const { label, title, onClick } = column.buttonOnHover;
      return (
        <div className="button-on-hover-cell">
          {Aut}
          <button
            className="button-on-hover"
            title={title}
            onClick={() => onClick(row.original)}
          >
            {label}
          </button>
        </div>
      );
    }
    return Aut;
  }

  // 7) buttonOnHover (no-autocomplete)
  if (column?.buttonOnHover) {
    const { label, title, onClick } = column.buttonOnHover;
    return (
      <div className="button-on-hover-cell">
        <span>{cell.value}</span>
        <button className="button-on-hover" title={title} onClick={() => onClick(row.original)}>
          {label}
        </button>
      </div>
    );
  }

  // 8) chips
  if (column?.chips) {
    const chips = Array.isArray(cell.value)
      ? cell.value
      : `${cell.value}`.split(",").map((c) => c.trim());
    return (
      <div
        className="chip-container"
        onDoubleClick={() => handleEditStart(row.original.id, column.accessor, cell.value)}
      >
        {chips.map((chip, i) => {
          const colorFn = column.chipColor;
          const bg =
            typeof colorFn === "function"
              ? colorFn(chip, row.original)
              : colorFn || "#e0e0e0";
          return (
            <span
              key={i}
              className="chip"
              style={{ backgroundColor: bg, color: getContrastColor(bg) }}
            >
              {chip}
            </span>
          );
        })}
      </div>
    );
  }

  // 9) datePicker
  if (column?.datePicker) {
    const editing =
      editingCell?.id === row.original.id && editingCell?.field === column.accessor;
    const dt = cell.value
      ? DateTime.fromISO(cell.value).setZone(
          Intl.DateTimeFormat().resolvedOptions().timeZone
        )
      : null;
    const localStr = dt ? dt.toFormat("yyyy-MM-dd'T'HH:mm") : "";
    const dispStr = dt
      ? dt.toFormat("MM/dd/yyyy h:mm a") + " " + dt.offsetNameShort
      : "";
    if (editing) {
      return (
        <input
          ref={inputRef}
          type="datetime-local"
          value={localStr}
          onChange={(e) => {
            const picked = DateTime.fromFormat(
              e.target.value,
              "yyyy-MM-dd'T'HH:mm",
              { zone: "local" }
            );
            handleEditChange(row.original.id, column.accessor, picked.toISO());
          }}
          onBlur={() => handleEditStart(null)}
          onKeyDown={(e) => handleKeyDown(e, rowIndex, columnIndex)}
        />
      );
    }
    return (
      <span onDoubleClick={() => handleEditStart(row.original.id, column.accessor)}>
        {dispStr}
      </span>
    );
  }

  // 10) chipDropdown
  if (column?.chipDropdown) {
    const key = `${row.original.id}-${column.accessor}`;
    const val = editedData[key] ?? cell.value;
    const colorFn = column.chipDropdownChipColor;
    const bg =
      typeof colorFn === "function" ? colorFn(row.original) : colorFn || "#e0e0e0";
    const textColor = getContrastColor(bg);
    return (
      <select
        style={{
          backgroundColor: bg,
          color: textColor,
          border: "none",
          borderRadius: "12px",
          padding: "2px 8px",
          fontSize: "12px",
          appearance: "none",
        }}
        value={val}
        onChange={(e) => handleEditChange(row.original.id, column.accessor, e.target.value)}
        onBlur={() => handleEditStart(null)}
        onKeyDown={(e) => handleKeyDown(e, rowIndex, columnIndex)}
      >
        {column.chipDropdownOptions.map((opt) => (
          <option key={opt[column.chipDropdownValueField]} value={opt[column.chipDropdownValueField]}>
            {column.chipDropdownDisplay ? column.chipDropdownDisplay(opt) : opt[column.chipDropdownValueField]}
          </option>
        ))}
      </select>
    );
  }

  // 11) fallback editable
  const key = `${row.original.id}-${column.accessor}`;
  const editing =
    editingCell?.id === row.original.id && editingCell?.field === column.accessor;
  const disp = editing ? editedData[key] ?? cell.value : cell.value;
  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={disp}
        onChange={(e) => handleEditChange(row.original.id, column.accessor, e.target.value)}
        onBlur={() => handleEditStart(null)}
        onKeyDown={(e) => handleKeyDown(e, rowIndex, columnIndex)}
      />
    );
  }
  return (
    <span
      className="editable-cell"
      data-rowid={row.original.id}
      data-field={column.accessor}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleEditStart(row.original.id, column.accessor, cell.value);
      }}
    >
      {cell.value}
    </span>
  );
},
(prev, next) => {
    // if this is the mass-selection column, always re-render
    const column = next.columns.find(
      (c) => c.accessor === next.cell.column.id
    );
    if (column?.massSelection) {
      return false;
    }
  
    const id    = next.row.original.id;
    const field = next.cell.column.id;
    const key   = `${id}-${field}`;
  
    const valueUnchanged     = prev.cell.value === next.cell.value;
    const draftUnchanged     = prev.editedData[key] === next.editedData[key];
    const editingUnchanged   =
      !(prev.editingCell?.id === id && prev.editingCell.field === field) &&
      !(next.editingCell?.id === id && next.editingCell.field === field);
    const selectionUnchanged =
      !!prev.selectedRows[id] === !!next.selectedRows[id];
  
    return (
      valueUnchanged &&
      draftUnchanged &&
      editingUnchanged &&
      selectionUnchanged
    );
  }
  
);
